import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import api from '@/utils/api';
import { setToken, setUser } from '@/utils/auth';
import { toast } from 'sonner';
import { GraduationCap, BookOpen, Shield, ArrowLeft, Mail, KeyRound, CheckCircle, Eye, EyeOff, ShoppingBag, Upload, User, Calendar, MapPin, FileText, Camera, Loader2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { uploadPublicFileToS3 } from '@/utils/s3Upload';

const MAX_QUALIFICATION_CERTS = 5;

export const AuthModal = ({ open, onClose, onSuccess, adminOnly = false, defaultRole = 'student', lockRole = false, defaultTab = 'login' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetStep, setResetStep] = useState('email'); // 'email', 'token', 'success'
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [selectedRole, setSelectedRole] = useState(defaultRole); // Track selected role for terms link
  
  // New registration form fields
  const [signupForm, setSignupForm] = useState({
    name: '',
    surname: '',
    email: '',
    password: '',
    confirmPassword: '',
    identity_number: '',
    date_of_birth: '',
    residential_address: '',
    gender: '',
  });
  
  // Document upload states
  const [identityDoc, setIdentityDoc] = useState(null);
  const [proofOfAddress, setProofOfAddress] = useState(null);
  const [facialPicture, setFacialPicture] = useState(null);
  const [uploadingIdentity, setUploadingIdentity] = useState(false);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [uploadingFacial, setUploadingFacial] = useState(false);
  const [qualificationCerts, setQualificationCerts] = useState([]);
  const [uploadingCerts, setUploadingCerts] = useState(false);
  
  // File input refs
  const identityDocRef = useRef(null);
  const proofOfAddressRef = useRef(null);
  const facialPictureRef = useRef(null);
  const qualificationCertsRef = useRef(null);
  
  // Update selected role when defaultRole prop changes
  useEffect(() => {
    setSelectedRole(defaultRole);
  }, [defaultRole]);

  useEffect(() => {
    if (open) setActiveTab(defaultTab);
  }, [open, defaultTab]);
  
  // Password visibility states
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  // Handle document upload using public upload endpoint (no auth required for signup)
  const handleDocumentUpload = async (file, type) => {
    if (!file) return null;
    
    // Validate file type
    const allowedTypes = type === 'facial' 
      ? ['image/jpeg', 'image/png', 'image/webp']
      : ['image/jpeg', 'image/png', 'application/pdf'];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error(`Invalid file type. Allowed: ${type === 'facial' ? 'JPEG, PNG, WebP' : 'JPEG, PNG, PDF'}`);
      return null;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return null;
    }
    
    try {
      // Use public upload endpoint (no auth required for pre-registration uploads)
      const folder = type === 'facial' ? 'avatars/' : 'documents/';
      const { promise } = uploadPublicFileToS3(file, folder);
      const result = await promise;
      
      // Result contains { url, file_key } directly from presigned response
      return { url: result.url, key: result.file_key, filename: file.name };
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file. Please try again.');
      return null;
    }
  };

  const handleIdentityDocChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingIdentity(true);
    const result = await handleDocumentUpload(file, 'identity');
    if (result) {
      setIdentityDoc(result);
      toast.success('Identity document uploaded');
    }
    setUploadingIdentity(false);
  };

  const handleProofOfAddressChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingProof(true);
    const result = await handleDocumentUpload(file, 'proof');
    if (result) {
      setProofOfAddress(result);
      toast.success('Proof of address uploaded');
    }
    setUploadingProof(false);
  };

  const handleFacialPictureChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingFacial(true);
    const result = await handleDocumentUpload(file, 'facial');
    if (result) {
      setFacialPicture(result);
      toast.success('Photo uploaded');
    }
    setUploadingFacial(false);
  };

  const handleQualificationCertsChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const remaining = MAX_QUALIFICATION_CERTS - qualificationCerts.length;
    if (files.length > remaining) {
      toast.error(`You can upload up to ${MAX_QUALIFICATION_CERTS} certificates`);
    }
    setUploadingCerts(true);
    for (const file of files.slice(0, Math.max(remaining, 0))) {
      const result = await handleDocumentUpload(file, 'qualification');
      if (result) setQualificationCerts((prev) => [...prev, result]);
    }
    setUploadingCerts(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const formData = new FormData(e.target);
    
    try {
      const response = await api.post('/auth/login', {
        email: formData.get('email'),
        password: formData.get('password'),
      });
      
      // If adminOnly mode, verify the user is actually an admin
      if (adminOnly && response.data.user.role !== 'admin') {
        toast.error('This login is for administrators only');
        setIsLoading(false);
        return;
      }
      
      setToken(response.data.token);
      setUser(response.data.user);
      toast.success(adminOnly ? 'Welcome, Admin!' : 'Welcome back!');
      onSuccess(response.data.user);
      onClose();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Login failed';
      
      // Check for account lockout (status 423)
      if (error.response?.status === 423) {
        toast.error(detail, { duration: 6000 });
      } else {
        toast.error(detail);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Validate passwords match
    if (signupForm.password !== signupForm.confirmPassword) {
      toast.error('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (!termsAccepted) {
      toast.error('You must accept the Terms of Service and Privacy Policy');
      setIsLoading(false);
      return;
    }

    // Validate required fields
    if (!signupForm.name.trim() || !signupForm.surname.trim()) {
      toast.error('Please enter your first name and surname');
      setIsLoading(false);
      return;
    }

    if (!signupForm.email.trim()) {
      toast.error('Please enter your email address');
      setIsLoading(false);
      return;
    }
    
    // Validate compulsory ID number
    if (!signupForm.identity_number?.trim()) {
      toast.error('Please enter your Identity/Passport Number');
      setIsLoading(false);
      return;
    }
    
    // Validate compulsory documents
    if (!identityDoc?.url) {
      toast.error('Please upload your Identity Document (ID/Passport)');
      setIsLoading(false);
      return;
    }
    
    if (!proofOfAddress?.url) {
      toast.error('Please upload your Proof of Address');
      setIsLoading(false);
      return;
    }
    
    if (!facialPicture?.url) {
      toast.error('Please upload your Profile Photo');
      setIsLoading(false);
      return;
    }
    
    try {
      const response = await api.post('/auth/signup', {
        email: signupForm.email,
        password: signupForm.password,
        name: signupForm.name,
        surname: signupForm.surname,
        identity_number: signupForm.identity_number,
        date_of_birth: signupForm.date_of_birth || null,
        residential_address: signupForm.residential_address || null,
        gender: signupForm.gender || null,
        identity_document_url: identityDoc.url,
        identity_document_key: identityDoc?.key || null,
        proof_of_address_url: proofOfAddress.url,
        proof_of_address_key: proofOfAddress?.key || null,
        avatar_url: facialPicture.url,  // Facial picture becomes profile pic
        qualification_certificates: qualificationCerts,
        role: selectedRole,
        terms_accepted: termsAccepted,
      });
      
      setToken(response.data.token);
      setUser(response.data.user);
      toast.success('Account created successfully!');
      onSuccess(response.data.user);
      onClose();
      
      // Reset form
      setSignupForm({
        name: '',
        surname: '',
        email: '',
        password: '',
        confirmPassword: '',
        identity_number: '',
        date_of_birth: '',
        residential_address: '',
        gender: '',
      });
      setIdentityDoc(null);
      setProofOfAddress(null);
      setFacialPicture(null);
      setQualificationCerts([]);
      setTermsAccepted(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await api.post('/auth/forgot-password', {
        email: forgotPasswordEmail,
      });
      
      toast.success('Password reset instructions sent to your email!');
      setResetStep('token');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset instructions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await api.post('/auth/reset-password', {
        token: resetToken,
        new_password: newPassword,
      });
      
      toast.success('Password reset successfully!');
      setResetStep('success');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForgotPasswordState = () => {
    setShowForgotPassword(false);
    setForgotPasswordEmail('');
    setResetToken('');
    setNewPassword('');
    setConfirmNewPassword('');
    setResetStep('email');
  };

  const renderForgotPassword = () => {
    if (resetStep === 'success') {
      return (
        <div className="space-y-4 pt-4 text-center">
          <div className="flex justify-center">
            <CheckCircle className="h-16 w-16" style={{ color: '#2E7D32' }} />
          </div>
          <h3 className="text-lg font-semibold" style={{ color: '#163329' }}>
            Password Reset Successfully!
          </h3>
          <p style={{ color: '#5C635A' }}>
            You can now log in with your new password.
          </p>
          <Button
            onClick={resetForgotPasswordState}
            className="w-full"
            
          >
            Back to Login
          </Button>
        </div>
      );
    }

    if (resetStep === 'token') {
      return (
        <form onSubmit={handleResetPassword} className="space-y-4 pt-4">
          <div className="flex items-center gap-2 mb-4">
            <button
              type="button"
              onClick={() => setResetStep('email')}
              className="text-sm flex items-center gap-1 hover:underline"
              style={{ color: '#5C635A' }}
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
          </div>
          
          <div className="p-4 rounded-lg text-center" style={{ backgroundColor: '#E8F5E9' }}>
            <CheckCircle className="h-12 w-12 mx-auto mb-3" style={{ color: '#4CAF50' }} />
            <h3 className="font-semibold mb-2" style={{ color: '#2E7D32' }}>Check Your Email</h3>
            <p className="text-sm" style={{ color: '#2E7D32' }}>
              We've sent password reset instructions to:
            </p>
            <p className="font-medium mt-1" style={{ color: '#163329' }}>{forgotPasswordEmail}</p>
          </div>
          
          <div className="p-3 rounded-lg" style={{ backgroundColor: '#FFF8E1', border: '1px solid #FFA000' }}>
            <p className="text-sm" style={{ color: '#F57C00' }}>
              <strong>Note:</strong> Click the link in your email to reset your password. The link expires in 1 hour.
            </p>
          </div>
          
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => {
              setShowForgotPassword(false);
              setResetStep('email');
              setForgotPasswordEmail('');
            }}
          >
            Back to Sign In
          </Button>
        </form>
      );
    }
    
    // Old token step - keeping for backwards compatibility but won't be shown normally
    if (resetStep === 'token_manual') {
      return (
        <form onSubmit={handleResetPassword} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reset-token">Reset Token</Label>
            <Input
              id="reset-token"
              type="text"
              placeholder="Enter reset token from email"
              value={resetToken}
              onChange={(e) => setResetToken(e.target.value)}
              required
              data-testid="reset-token-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showNewPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                data-testid="new-password-input"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: '#5C635A' }}
                data-testid="toggle-new-password"
              >
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirm-new-password">Confirm New Password</Label>
            <div className="relative">
              <Input
                id="confirm-new-password"
                type={showConfirmNewPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                minLength={6}
                data-testid="confirm-new-password-input"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: '#5C635A' }}
                data-testid="toggle-confirm-new-password"
              >
                {showConfirmNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
            
            data-testid="reset-password-submit"
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>
      );
    }

    return (
      <form onSubmit={handleForgotPassword} className="space-y-4 pt-4">
        <div className="flex items-center gap-2 mb-4">
          <button
            type="button"
            onClick={resetForgotPasswordState}
            className="text-sm flex items-center gap-1 hover:underline"
            style={{ color: '#5C635A' }}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Login
          </button>
        </div>
        
        <div className="text-center mb-4">
          <KeyRound className="h-12 w-12 mx-auto mb-2" style={{ color: '#163329' }} />
          <p style={{ color: '#5C635A' }}>
            Enter your email address and we'll send you instructions to reset your password.
          </p>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="forgot-email">Email Address</Label>
          <Input
            id="forgot-email"
            type="email"
            placeholder="you@example.com"
            value={forgotPasswordEmail}
            onChange={(e) => setForgotPasswordEmail(e.target.value)}
            required
            data-testid="forgot-email-input"
          />
        </div>
        
        <Button
          type="submit"
          className="w-full"
          disabled={isLoading}
          
          data-testid="forgot-password-submit"
        >
          {isLoading ? 'Sending...' : 'Send Reset Instructions'}
        </Button>
      </form>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          {showForgotPassword ? (
            <>
              <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Spectral, serif' }}>
                Reset Password
              </DialogTitle>
              <DialogDescription>Recover access to your account</DialogDescription>
            </>
          ) : adminOnly ? (
            <>
              <DialogTitle className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Spectral, serif' }}>
                <Shield className="h-6 w-6" style={{ color: '#163329' }} />
                Admin Access
              </DialogTitle>
              <DialogDescription>Sign in with your administrator credentials</DialogDescription>
            </>
          ) : (
            <>
              <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Spectral, serif' }}>Welcome</DialogTitle>
              <DialogDescription>Sign in to access your learning platform</DialogDescription>
            </>
          )}
        </DialogHeader>
        
        {showForgotPassword ? (
          renderForgotPassword()
        ) : adminOnly ? (
          // Admin login only - no tabs, no signup
          <form onSubmit={handleLogin} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="admin-email">Admin Email</Label>
              <Input
                id="admin-email"
                name="email"
                type="email"
                placeholder="admin@example.com"
                required
                data-testid="admin-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password">Password</Label>
              <div className="relative">
                <Input
                  id="admin-password"
                  name="password"
                  type={showAdminPassword ? "text" : "password"}
                  required
                  data-testid="admin-password-input"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowAdminPassword(!showAdminPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: '#5C635A' }}
                  data-testid="toggle-admin-password"
                >
                  {showAdminPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="text-right">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm hover:underline"
                style={{ color: '#D86B47' }}
                data-testid="admin-forgot-password-link"
              >
                Forgot password?
              </button>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
              data-testid="admin-login-submit"
              
            >
              {isLoading ? 'Signing in...' : 'Sign In as Admin'}
            </Button>
          </form>
        ) : (
          // Regular auth with tabs for login/signup
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="signup" data-testid="signup-tab">Sign Up</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    required
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      name="password"
                      type={showLoginPassword ? "text" : "password"}
                      required
                      data-testid="login-password-input"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      style={{ color: '#5C635A' }}
                      data-testid="toggle-login-password"
                    >
                      {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm hover:underline"
                    style={{ color: '#D86B47' }}
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                  data-testid="login-submit-button"
                  
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="signup">
              <ScrollArea className="h-[60vh] pr-4">
              <form onSubmit={handleSignup} className="space-y-4 pb-4">
                {/* Personal Information Section */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: '#163329' }}>
                    <User className="h-4 w-4" />
                    Personal Information
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="signup-name" className="text-xs">First Name *</Label>
                      <Input
                        id="signup-name"
                        type="text"
                        placeholder="John"
                        required
                        value={signupForm.name}
                        onChange={(e) => setSignupForm(prev => ({ ...prev, name: e.target.value }))}
                        data-testid="signup-name-input"
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="signup-surname" className="text-xs">Surname *</Label>
                      <Input
                        id="signup-surname"
                        type="text"
                        placeholder="Doe"
                        required
                        value={signupForm.surname}
                        onChange={(e) => setSignupForm(prev => ({ ...prev, surname: e.target.value }))}
                        data-testid="signup-surname-input"
                        className="h-9"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <Label htmlFor="signup-email" className="text-xs">Email *</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      required
                      value={signupForm.email}
                      onChange={(e) => setSignupForm(prev => ({ ...prev, email: e.target.value }))}
                      data-testid="signup-email-input"
                      className="h-9"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="signup-dob" className="text-xs">Date of Birth</Label>
                      <Input
                        id="signup-dob"
                        type="date"
                        value={signupForm.date_of_birth}
                        onChange={(e) => setSignupForm(prev => ({ ...prev, date_of_birth: e.target.value }))}
                        data-testid="signup-dob-input"
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="signup-gender" className="text-xs">Gender</Label>
                      <Select
                        value={signupForm.gender}
                        onValueChange={(value) => setSignupForm(prev => ({ ...prev, gender: value }))}
                      >
                        <SelectTrigger className="h-9" data-testid="signup-gender-select">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <Label htmlFor="signup-id-number" className="text-xs">Identity/Passport Number *</Label>
                    <Input
                      id="signup-id-number"
                      type="text"
                      placeholder="ID or Passport Number"
                      required
                      value={signupForm.identity_number}
                      onChange={(e) => setSignupForm(prev => ({ ...prev, identity_number: e.target.value }))}
                      data-testid="signup-id-number-input"
                      className="h-9"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label htmlFor="signup-address" className="text-xs">Residential Address</Label>
                    <Textarea
                      id="signup-address"
                      placeholder="Enter your full residential address"
                      value={signupForm.residential_address}
                      onChange={(e) => setSignupForm(prev => ({ ...prev, residential_address: e.target.value }))}
                      data-testid="signup-address-input"
                      rows={2}
                      className="resize-none text-sm"
                    />
                  </div>
                </div>
                
                {/* Document Uploads Section */}
                <div className="space-y-3 pt-2 border-t" style={{ borderColor: '#E3E1DB' }}>
                  <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: '#163329' }}>
                    <FileText className="h-4 w-4" />
                    Documents (Compulsory)
                  </h3>
                  
                  {/* Facial Picture / Profile Photo */}
                  <div className="space-y-1">
                    <Label className="text-xs flex items-center gap-1">
                      <Camera className="h-3 w-3" />
                      Profile Photo
                    </Label>
                    <div className="flex items-center gap-3">
                      {facialPicture ? (
                        <div className="relative">
                          <img 
                            src={facialPicture.url} 
                            alt="Profile" 
                            className="w-16 h-16 rounded-full object-cover border-2"
                            style={{ borderColor: '#163329' }}
                          />
                          <button
                            type="button"
                            onClick={() => setFacialPicture(null)}
                            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <div 
                          className="w-16 h-16 rounded-full border-2 border-dashed flex items-center justify-center cursor-pointer hover:bg-gray-50"
                          style={{ borderColor: '#E3E1DB' }}
                          onClick={() => facialPictureRef.current?.click()}
                        >
                          {uploadingFacial ? (
                            <Loader2 className="h-5 w-5 animate-spin" style={{ color: '#5C635A' }} />
                          ) : (
                            <Camera className="h-5 w-5" style={{ color: '#5C635A' }} />
                          )}
                        </div>
                      )}
                      <div className="flex-1">
                        <input
                          ref={facialPictureRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleFacialPictureChange}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => facialPictureRef.current?.click()}
                          disabled={uploadingFacial}
                          className="text-xs"
                          data-testid="upload-facial-btn"
                        >
                          {uploadingFacial ? 'Uploading...' : facialPicture ? 'Change Photo' : 'Upload Photo'}
                        </Button>
                        <p className="text-xs mt-1" style={{ color: '#5C635A' }}>JPEG, PNG or WebP</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Identity Document */}
                  <div className="space-y-1">
                    <Label className="text-xs">Identity Document (ID/Passport)</Label>
                    <div 
                      className="border-2 border-dashed rounded-lg p-3 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                      style={{ borderColor: identityDoc ? '#4CAF50' : '#E3E1DB' }}
                      onClick={() => identityDocRef.current?.click()}
                      data-testid="upload-identity-dropzone"
                    >
                      <input
                        ref={identityDocRef}
                        type="file"
                        accept="image/jpeg,image/png,application/pdf"
                        onChange={handleIdentityDocChange}
                        className="hidden"
                        data-testid="upload-identity-input"
                      />
                      {uploadingIdentity ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" style={{ color: '#163329' }} />
                          <span className="text-xs" style={{ color: '#5C635A' }}>Uploading...</span>
                        </div>
                      ) : identityDoc ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" style={{ color: '#4CAF50' }} />
                            <span className="text-xs font-medium truncate max-w-[180px]" style={{ color: '#2E7D32' }}>
                              {identityDoc.filename}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setIdentityDoc(null); }}
                            className="text-red-500 hover:text-red-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <Upload className="h-5 w-5" style={{ color: '#5C635A' }} />
                          <span className="text-xs" style={{ color: '#5C635A' }}>Click to upload ID document</span>
                          <span className="text-xs" style={{ color: '#9CA3AF' }}>PDF, JPEG or PNG (max 10MB)</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Proof of Address */}
                  <div className="space-y-1">
                    <Label className="text-xs">Proof of Address</Label>
                    <div 
                      className="border-2 border-dashed rounded-lg p-3 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                      style={{ borderColor: proofOfAddress ? '#4CAF50' : '#E3E1DB' }}
                      onClick={() => proofOfAddressRef.current?.click()}
                      data-testid="upload-proof-dropzone"
                    >
                      <input
                        ref={proofOfAddressRef}
                        type="file"
                        accept="image/jpeg,image/png,application/pdf"
                        onChange={handleProofOfAddressChange}
                        className="hidden"
                        data-testid="upload-proof-input"
                      />
                      {uploadingProof ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" style={{ color: '#163329' }} />
                          <span className="text-xs" style={{ color: '#5C635A' }}>Uploading...</span>
                        </div>
                      ) : proofOfAddress ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" style={{ color: '#4CAF50' }} />
                            <span className="text-xs font-medium truncate max-w-[180px]" style={{ color: '#2E7D32' }}>
                              {proofOfAddress.filename}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setProofOfAddress(null); }}
                            className="text-red-500 hover:text-red-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <MapPin className="h-5 w-5" style={{ color: '#5C635A' }} />
                          <span className="text-xs" style={{ color: '#5C635A' }}>Click to upload proof of address</span>
                          <span className="text-xs" style={{ color: '#9CA3AF' }}>Utility bill, bank statement, etc.</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Qualification Certificates */}
                <div className="space-y-1" data-testid="qualification-certs-section">
                  <Label className="text-xs">Recent Qualification Certificates (optional, up to {MAX_QUALIFICATION_CERTS})</Label>
                  <div
                    className="border-2 border-dashed rounded-lg p-3 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                    style={{ borderColor: qualificationCerts.length ? '#4CAF50' : '#E3E1DB' }}
                    onClick={() => qualificationCerts.length < MAX_QUALIFICATION_CERTS && qualificationCertsRef.current?.click()}
                    data-testid="upload-qualification-dropzone"
                  >
                    <input
                      ref={qualificationCertsRef}
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,application/pdf"
                      onChange={handleQualificationCertsChange}
                      className="hidden"
                      data-testid="upload-qualification-input"
                    />
                    {uploadingCerts ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" style={{ color: '#163329' }} />
                        <span className="text-xs" style={{ color: '#5C635A' }}>Uploading...</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <GraduationCap className="h-5 w-5" style={{ color: '#5C635A' }} />
                        <span className="text-xs" style={{ color: '#5C635A' }}>
                          {qualificationCerts.length >= MAX_QUALIFICATION_CERTS ? 'Maximum reached' : 'Click to upload certificates'}
                        </span>
                        <span className="text-xs" style={{ color: '#9CA3AF' }}>PDF, JPEG or PNG (max 10MB each)</span>
                      </div>
                    )}
                  </div>
                  {qualificationCerts.map((cert, idx) => (
                    <div key={cert.key || idx} className="flex items-center justify-between px-2 py-1 rounded" style={{ backgroundColor: '#F1F8F1' }} data-testid={`qualification-cert-${idx}`}>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" style={{ color: '#4CAF50' }} />
                        <span className="text-xs font-medium truncate max-w-[200px]" style={{ color: '#2E7D32' }}>{cert.filename}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setQualificationCerts((prev) => prev.filter((_, i) => i !== idx))}
                        className="text-red-500 hover:text-red-600"
                        data-testid={`remove-qualification-cert-${idx}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Password Section */}
                <div className="space-y-3 pt-2 border-t" style={{ borderColor: '#E3E1DB' }}>
                  <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: '#163329' }}>
                    <KeyRound className="h-4 w-4" />
                    Account Security
                  </h3>
                  
                  <div className="space-y-1">
                    <Label htmlFor="signup-password" className="text-xs">Password *</Label>
                    <div className="relative">
                      <Input
                        id="signup-password"
                        type={showSignupPassword ? "text" : "password"}
                        required
                        minLength={8}
                        value={signupForm.password}
                        onChange={(e) => setSignupForm(prev => ({ ...prev, password: e.target.value }))}
                        data-testid="signup-password-input"
                        className="pr-10 h-9"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSignupPassword(!showSignupPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                        style={{ color: '#5C635A' }}
                        data-testid="toggle-signup-password"
                      >
                        {showSignupPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-xs" style={{ color: '#5C635A' }}>
                      Min 8 characters with at least one uppercase letter
                    </p>
                  </div>
                  
                  <div className="space-y-1">
                    <Label htmlFor="signup-confirm-password" className="text-xs">Confirm Password *</Label>
                    <div className="relative">
                      <Input
                        id="signup-confirm-password"
                        type={showSignupConfirmPassword ? "text" : "password"}
                        required
                        minLength={8}
                        value={signupForm.confirmPassword}
                        onChange={(e) => setSignupForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        data-testid="signup-confirm-password-input"
                        className="pr-10 h-9"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSignupConfirmPassword(!showSignupConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                        style={{ color: '#5C635A' }}
                        data-testid="toggle-signup-confirm-password"
                      >
                        {showSignupConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Role Selection - Hidden when lockRole is true */}
                {!lockRole && (
                  <div className="space-y-2 pt-2 border-t" style={{ borderColor: '#E3E1DB' }}>
                    <Label className="text-xs font-semibold" style={{ color: '#163329' }}>I am registering as:</Label>
                    <div className="flex flex-wrap gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="role" 
                          value="student" 
                          checked={selectedRole === 'student'}
                          data-testid="role-student"
                          onChange={() => setSelectedRole('student')}
                        />
                        <BookOpen className="h-4 w-4" />
                        <span className="text-sm">Learner</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="role" 
                          value="teacher" 
                          checked={selectedRole === 'teacher'}
                          data-testid="role-teacher"
                          onChange={() => setSelectedRole('teacher')}
                        />
                        <GraduationCap className="h-4 w-4" />
                        <span className="text-sm">Teacher</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="role" 
                          value="seller" 
                          checked={selectedRole === 'seller'}
                          data-testid="role-seller"
                          onChange={() => setSelectedRole('seller')}
                        />
                        <ShoppingBag className="h-4 w-4" />
                        <span className="text-sm">Seller</span>
                      </label>
                    </div>
                  </div>
                )}
                
                {/* Terms Acceptance - Dynamic based on role */}
                <div className="flex items-start space-x-2 pt-2">
                  <Checkbox
                    id="terms"
                    checked={termsAccepted}
                    onCheckedChange={setTermsAccepted}
                    data-testid="terms-checkbox"
                    className="mt-0.5"
                  />
                  <label htmlFor="terms" className="text-xs leading-tight cursor-pointer" style={{ color: '#5C635A' }}>
                    I agree to the{' '}
                    <Link 
                      to={`/legal?role=${selectedRole === 'teacher' || selectedRole === 'seller' ? selectedRole : 'learner'}&section=terms`} 
                      target="_blank" 
                      className="underline font-medium"
                      style={{ color: '#163329' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {selectedRole === 'teacher' ? 'Teacher Terms of Service' : selectedRole === 'seller' ? 'Seller Terms of Service' : 'Terms of Service'}
                    </Link>
                    {' '}and{' '}
                    <Link 
                      to={`/legal?role=${selectedRole === 'teacher' || selectedRole === 'seller' ? selectedRole : 'learner'}&section=privacy`} 
                      target="_blank" 
                      className="underline font-medium"
                      style={{ color: '#163329' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Privacy Policy
                    </Link>
                  </label>
                </div>
                
                <Button
                  type="submit"
                  className="w-full"
                  disabled={
                    isLoading || 
                    !termsAccepted || 
                    uploadingIdentity || 
                    uploadingProof || 
                    uploadingFacial ||
                    !signupForm.identity_number?.trim() ||
                    !identityDoc?.url ||
                    !proofOfAddress?.url ||
                    !facialPicture?.url
                  }
                  data-testid="signup-submit-button"
                >
                  {isLoading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
